package dev.kuml.portal.web.editor

import kotlin.math.max
import kotlin.math.min
import kotlin.math.round

/**
 * Pure raster-size math for R-7's client-side PNG export -- no DOM, so the reference-width and
 * canvas-cap rules are directly unit-testable (see `PngExportMathTest`) instead of only observable
 * by downloading a file from a running browser.
 *
 * [REFERENCE_WIDTH_PX] mirrors the kUML main repo's `SampleOutput.write()` convention of rasterising
 * against a fixed reference width -- but as a FLOOR, not a target: a diagram whose native SVG size
 * is already wider than 1200 px is never downscaled, because shrinking it would destroy exactly the
 * label legibility the export exists to preserve. Upscaling is free of quality loss here (the source
 * is vector), so a small diagram is rasterised up to the reference width and comes out crisp.
 */
public object PngExportMath {
    /** Minimum raster width in device pixels -- see this object's KDoc for why this is a floor. */
    public const val REFERENCE_WIDTH_PX: Int = 1200

    /** Conservative cross-platform maximum canvas edge; beyond this browsers start returning a
     * blank or allocation-failed canvas. Same value `obsidian-kuml`'s `downloadPng()` uses. */
    public const val MAX_CANVAS_EDGE_PX: Int = 8192

    public data class RasterSize(
        val width: Int,
        val height: Int,
        val scale: Double,
    )

    /**
     * Raster dimensions for an SVG of the given natural pixel size, or `null` for a degenerate
     * input (zero/negative extent -- e.g. an `<img>` that has not finished decoding, which the
     * caller must treat as "cannot export right now" rather than exporting a 1x1 pixel file).
     */
    public fun computeRasterSize(
        naturalWidth: Double,
        naturalHeight: Double,
    ): RasterSize? {
        if (naturalWidth <= 0 || naturalHeight <= 0) return null
        val cap = MAX_CANVAS_EDGE_PX.toDouble()
        var scale = max(1.0, REFERENCE_WIDTH_PX / naturalWidth)
        if (naturalWidth * scale > cap || naturalHeight * scale > cap) {
            scale = min(cap / naturalWidth, cap / naturalHeight)
        }
        val width = max(1, round(naturalWidth * scale).toInt())
        val height = max(1, round(naturalHeight * scale).toInt())
        return RasterSize(width, height, scale)
    }
}
